<script setup lang="ts">
import type { ApalumaChatThread } from '@/types/api-response/chat-session-types';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { Icon } from '@iconify/vue';
import { reactive, ref, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import ModalDeleteSession from '@/components/home-page/chat/sidebar/modal-delete-session.vue';
import ModalRenameSession from '@/components/home-page/chat/sidebar/modal-rename-session.vue';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
   session: ApalumaChatThread;
   deleteSession: (sessionId: number) => Promise<boolean>;
   renameSession: (sessionId: number, title: string) => Promise<boolean>;
   triggerAriaLabel: string;
}>();

const isDropdownOpen = ref(false);
const referenceEl = ref<HTMLElement | null>(null);
const floatingEl = ref<HTMLElement | null>(null);

const { floatingStyles } = useFloating(referenceEl, floatingEl, {
   placement: 'right-start',
   middleware: [offset(4), flip(), shift({ padding: 8 })],
   whileElementsMounted: autoUpdate,
});

const deleteModalState = reactive({
   isOpen: false,
   error: null as string | null,
});

const renameModalState = reactive({
   isOpen: false,
   error: null as string | null,
});

function toggleDropdown(event: Event) {
   if (isDropdownOpen.value) {
      closeDropdown();
   }
   else {
      referenceEl.value = event.currentTarget as HTMLElement;
      isDropdownOpen.value = true;
   }
}

function closeDropdown() {
   isDropdownOpen.value = false;
   referenceEl.value = null;
}

function openDeleteModal() {
   closeDropdown();
   deleteModalState.error = null;
   deleteModalState.isOpen = true;
}

function openRenameModal() {
   closeDropdown();
   renameModalState.error = null;
   renameModalState.isOpen = true;
}

async function handleDelete() {
   deleteModalState.error = null;
   try {
      const success = await props.deleteSession(props.session.id);
      if (success) {
         deleteModalState.isOpen = false;
      }
      else {
         deleteModalState.error = 'Failed to delete chat session';
      }
   }
   catch (e) {
      console.error(e);
      deleteModalState.error = 'Failed to delete chat session';
   }
}

async function handleRename(title: string) {
   renameModalState.error = null;
   try {
      const success = await props.renameSession(props.session.id, title);
      if (success) {
         renameModalState.isOpen = false;
      }
      else {
         renameModalState.error = 'Failed to rename chat session';
      }
   }
   catch (e) {
      console.error(e);
      renameModalState.error = 'Failed to rename chat session';
   }
}

// Reset modal state when closed
watch(() => deleteModalState.isOpen, (isOpen) => {
   if (!isOpen) {
      deleteModalState.error = null;
   }
});

watch(() => renameModalState.isOpen, (isOpen) => {
   if (!isOpen) {
      renameModalState.error = null;
   }
});
</script>

<template>
   <!-- Trigger button -->
   <button
      v-bind="$attrs"
      :aria-label="triggerAriaLabel"
      :class="{ '!opacity-100 !pointer-events-auto': isDropdownOpen }"
      @click.stop="toggleDropdown($event)"
   >
      <Icon :icon="IconLib.actions.options" class="size-5 text-black" />
   </button>

   <!-- Floating Dropdown (teleported to body) -->
   <Teleport to="body">
      <div
         v-if="isDropdownOpen"
         class="fixed inset-0 z-[60]"
         @click="closeDropdown"
      />
      <ul
         v-if="isDropdownOpen"
         ref="floatingEl"
         :style="floatingStyles"
         class="bg-base-100 rounded-xl w-36 z-[70] text-base font-medium
         flex flex-col gap-2 p-1.5 text-left list-none border border-neutral"
      >
         <li>
            <button
               class="flex items-center gap-2 px-2 py-1 hover:bg-gray-200 rounded-lg w-full"
               @click="openRenameModal"
            >
               <Icon :icon="IconLib.actions.edit" class="size-5" />
               Rename
            </button>
         </li>
         <li>
            <button
               class="flex items-center gap-2 text-red-600 hover:bg-red-50 rounded-lg px-2 py-1 w-full"
               @click="openDeleteModal"
            >
               <Icon :icon="IconLib.actions.delete" class="size-5" />
               Delete
            </button>
         </li>
      </ul>
   </Teleport>

   <!-- Delete Modal -->
   <ModalDeleteSession
      v-model="deleteModalState.isOpen"
      :session="session"
      :error="deleteModalState.error"
      @delete="handleDelete"
   />

   <!-- Rename Modal -->
   <ModalRenameSession
      v-model="renameModalState.isOpen"
      :session="session"
      :error="renameModalState.error"
      @rename="handleRename"
   />
</template>
